/* ══════════════════════════════════════════════════════════════════
   **Nie ein falscher Leerzustand** — der eine Baustein dafür
   (Prüfrunde Runde 10, N10-03; Runde 11, N11-01; 05.10.2026).

   Ein Satz, der „es gibt nichts" behauptet, und eine Null in einem Reiter
   sind **Aussagen über eine Antwort**. Solange die Abfrage unterwegs ist,
   gibt es diese Antwort nicht. Am Telefon stand 2,3 s lang „Heute geht
   nichts raus und kommt nichts rein.", bevor „9 rein" kam; die Angebotsliste
   zeigte „Offen 0 · … · Alle 0", bevor „Offen 1 · … · Alle 22" kam — nach
   N10-03 dasselbe Muster an anderen Stellen.

   **Die Regel, an jeder Stelle gleich:**

   | Stand der Abfrage | Leersatz | Zahl |
   |---|---|---|
   | noch keine Antwort | „Wird geholt …" (`allgemein.laedt`) | keine (`null` → „…" oder nichts) |
   | Fehler, keine ältere Antwort | der Fehlersatz der Stelle | keine |
   | eine Antwort (auch eine ältere, während neu geholt wird) | der Leersatz | die Zahl |

   Eine ältere Antwort bleibt eine Antwort: Wer neu holt, sieht weiter, was
   zuletzt galt, statt eines Flackerns.

   Der Stand reicht als drei Wahrheitswerte durch (`Ladestand`), damit auch
   Bausteine ohne React-Query (die Fallspalten, eine Zeile im Kopf) dieselbe
   Frage stellen können. `ladestandVon` macht ihn aus einer Abfrage.
   ══════════════════════════════════════════════════════════════════ */

/** Was eine Stelle über ihre Abfrage wissen muss, um „nichts" sagen zu dürfen. */
export type Ladestand = { isPending: boolean; isError: boolean; hatDaten: boolean };

/**
 * Der Stand aus einer React-Query-Abfrage (oder etwas, das so aussieht).
 *
 * **Eine gehaltene Antwort auf eine andere Frage ist keine** (N12-01,
 * 05.10.2026): `placeholderData` hält beim Nachschlagen die Treffer des
 * vorigen Wortes, bis die neuen da sind. Sind die leer, stand kurz „Nichts
 * gefunden zu „Mulde"." — die Antwort galt einem anderen Wort. Dieselbe
 * Frage, neu geholt, ist dagegen eine Antwort (kein Platzhalter).
 */
export function ladestandVon(abfrage: {
  isPending: boolean; isError: boolean; data?: unknown; isPlaceholderData?: boolean;
}): Ladestand {
  return {
    isPending: abfrage.isPending,
    isError: abfrage.isError,
    hatDaten: abfrage.data !== undefined && abfrage.isPlaceholderData !== true,
  };
}

/** Mehrere Abfragen hinter einer Stelle: geantwortet hat sie erst, wenn alle geantwortet haben. */
export function ladestandAlle(...staende: Ladestand[]): Ladestand {
  return {
    isPending: staende.some((s) => s.isPending),
    isError: staende.some((s) => s.isError && !s.hatDaten),
    hatDaten: staende.every((s) => s.hatDaten),
  };
}

/** Der Fehlersatz, wenn die Stelle keinen eigenen hat. */
export const LADEFEHLER = "allgemein.ladefehlerKurz";

/**
 * **Welcher Satz an einer leeren Stelle steht** — ein i18n-Schlüssel.
 * Erst eine Antwort ohne Zeilen ist ein Leerzustand.
 */
export function leersatzFuer(stand: Ladestand, leer: string, fehler: string = LADEFEHLER): string {
  if (stand.hatDaten) return leer;
  if (stand.isError) return fehler;
  return "allgemein.laedt";
}

/**
 * **Eine Zahl, die aus einer Abfrage kommt** — `null`, solange es keine
 * Antwort gibt. Wer sie zeigt, zeigt dann nichts oder „…", nie eine 0.
 */
export function zahlFuer(stand: Ladestand, zahl: number): number | null {
  return stand.hatDaten ? zahl : null;
}
